/**
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Navbar from "components/layout/Navbar";
import Sidebar from "components/layout/Sidebar";
import MobileTopNavbar from "components/layout/MobileTopNavbar";
import MobileBottomNavbar from "components/layout/MobileBottomNavbar";
import Footer from "components/layout/Footer";
import SignInModal from "components/auth/SignInModal";
import SignUpModal from "components/auth/SignUpModal";
import ForgotPasswordModal from "components/auth/ForgotPasswordModal";
import CookieConsentBanner from "components/CookieConsentBanner";

import HomePage from "pages/HomePage";
import DashboardPage from "pages/DashboardPage";
import PrivateRoute from "components/auth/PrivateRoute";
import { useAuth } from "./AuthContext";
import { useAppSelector } from "./store/hooks";
import { selectUser } from "./store/userSlice";
import ClubsPage from "pages/ClubsPage";
import ClubLandingPage from "pages/ClubLandingPage";
import AdminConsolePage from "pages/AdminConsolePage";
import AdminRoute from "components/auth/AdminRoute";
import ProtectedRoute from "components/ProtectedRoute";
import NotAuthorized from "pages/NotAuthorized";
import EditClubPage from "pages/EditClubPage";
import EditEventPage from "pages/EditEventPage";
import MajorLandingPage from "pages/MajorLandingPage";
import UserSettingsPage from "pages/UserSettingsPage";
import CreateClubPage from "pages/CreateClubPage";
import MerchPage from "pages/MerchPage";
import SponsorPage from "pages/SponsorPage";
import MixCupPage from "pages/MixCupPage";
import EventLandingPage from "pages/EventLandingPage";
import CheckoutPage from "pages/CheckoutPage";
import NotFoundPage from "pages/NotFoundPage";
import PrivacyPolicyPage from "pages/PrivacyPolicyPage";
import TermsOfServicePage from "pages/TermsOfServicePage";
import AccessibilityPage from "pages/AccessibilityPage";
import { ThemeProvider } from "./ThemeContext";
import { AuthModalProvider, useAuthModal } from "./contexts/AuthModalContext";
import { useMediaQuery, useTheme } from "@mui/material";

/** Legacy /login URLs: stay on site and open the sign-in modal instead. */
function LoginRedirect() {
  const { openSignIn } = useAuthModal();

  useEffect(() => {
    openSignIn();
  }, [openSignIn]);

  return <Navigate to="/" replace />;
}

export default function App() {
  const { login, logout } = useAuth();
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [signInOpen, setSignInOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const openSignIn = () => {
    setSignUpOpen(false);
    setForgotPasswordOpen(false);
    setSignInOpen(true);
  };

  const openSignUp = () => {
    setSignInOpen(false);
    setForgotPasswordOpen(false);
    setSignUpOpen(true);
  };

  const handleSignIn = async (credentials) => {
    try {
      await login(credentials);
      setSignInOpen(false);
    } catch (error) {
      console.error("Sign in error:", error);
      // Re-throw the error so SignInModal can handle it
      throw error;
    }
  };

  const handleSignUp = async (userData) => {
    try {
      if (userData?.googleUser) {
        await login(userData);
      }
      setSignUpOpen(false);
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleResetPassword = (data) => {
    // TODO: Implement actual password reset functionality
    setForgotPasswordOpen(false);
  };

  const handleOpenSettings = () => {
    navigate("/settings");
  };

  useEffect(() => {
    // Check both search params and hash params
    const urlParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace('#', '?'));
    
    const paymentStatus = urlParams.get('payment') || hashParams.get('payment');
    const eventId = urlParams.get('event_id') || hashParams.get('event_id');
    const sessionId = urlParams.get('session_id') || hashParams.get('session_id');

    if (paymentStatus === 'success') {
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success message
      alert(`Payment successful! Session ID: ${sessionId}`);
      
      // Navigate to the event page
      if (eventId) {
        navigate(`/events/${eventId}`);
      } else {
        navigate('/');
      }
    } else if (paymentStatus === 'cancelled') {
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show cancellation message
      alert('Payment was cancelled.');
      
      // Navigate to the event page
      if (eventId) {
        navigate(`/events/${eventId}`);
      } else {
        navigate('/');
      }
    }
  }, [location.search, location.hash, navigate]);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <ThemeProvider>
        <AuthModalProvider value={{ openSignIn, openSignUp, setSignInOpen, setSignUpOpen }}>
        <CssBaseline />
        <Box sx={{ display: "flex" }}>
          {/* Desktop Sidebar - Hidden on mobile */}
          {!isMobile && (
            <Sidebar
              onLogout={handleLogout}
              onOpenSignIn={openSignIn}
              onOpenSignUp={openSignUp}
              onOpenSettings={handleOpenSettings}
            />
          )}
          
          {/* Mobile Top Navbar - Only shown on mobile */}
          <MobileTopNavbar
            onLogout={handleLogout}
            onOpenSignIn={openSignIn}
            onOpenSignUp={openSignUp}
            onOpenSettings={handleOpenSettings}
          />
          
          <Box sx={{ 
            flexGrow: 1, 
            display: "flex", 
            flexDirection: "column", 
            minHeight: "100vh",
            // Add top padding on mobile for fixed navbar
            pt: { xs: 7, md: 0 }
          }}>
            {/* Desktop Navbar - Hidden on mobile */}
            {!isMobile && <Navbar />}
            
            <Box component="main" sx={{ 
              flexGrow: 1, 
              p: { xs: 1, sm: 2, md: 3 },
              // Add bottom padding on mobile for fixed bottom navbar
              pb: { xs: 8, md: 3 }
            }}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginRedirect />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <PrivateRoute>
                      <DashboardPage />
                    </PrivateRoute>
                  } 
                />
                <Route path="/clubs" element={<ClubsPage />} />
                <Route path="/clubs/:handle" element={<ClubLandingPage />} />
                <Route path="/events/:eventId" element={<EventLandingPage />} />
                <Route 
                  path="/checkout/:eventId" 
                  element={
                    <PrivateRoute>
                      <CheckoutPage />
                    </PrivateRoute>
                  } 
                />
                <Route path="/admin" element={
                  <AdminRoute>
                    <AdminConsolePage />
                  </AdminRoute>
                } />
                <Route path="/clubs/:id/edit" element={
                  <ProtectedRoute>
                    <EditClubPage />
                  </ProtectedRoute>
                } />
                <Route path="/edit-event/:eventId" element={
                  <ProtectedRoute>
                    <EditEventPage />
                  </ProtectedRoute>
                } />
                <Route path="/majors/:id" element={<MajorLandingPage />} />
                <Route path="/bucha" element={<Navigate to="/majors/41aee5e8-e57f-47b7-80b5-ba52e9f4990e" replace />} />
                <Route path="/settings" element={
                  <PrivateRoute>
                    <UserSettingsPage />
                  </PrivateRoute>
                } />
                <Route path="/create-club" element={<CreateClubPage />} />
                <Route path="/merch" element={<MerchPage />} />
                <Route path="/sponsor" element={<SponsorPage />} />
                <Route path="/mixcup" element={<MixCupPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/accessibility" element={<AccessibilityPage />} />
                <Route path="/not-authorized" element={<NotAuthorized />} />
                
                {/* Catch-all route for 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </Box>

        {/* Mobile Bottom Navbar - Only shown on mobile */}
        <MobileBottomNavbar />

      <SignInModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        onSignIn={handleSignIn}
        onSwitchToSignUp={openSignUp}
        onSwitchToForgotPassword={() => {
          setSignInOpen(false);
          setForgotPasswordOpen(true);
        }}
      />

      <SignUpModal
        open={signUpOpen}
        onClose={() => setSignUpOpen(false)}
        onSignUp={handleSignUp}
        onSwitchToSignIn={openSignIn}
      />

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        onResetPassword={handleResetPassword}
        onSwitchToSignIn={openSignIn}
      />

      <CookieConsentBanner />
        </AuthModalProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
